import ConfirmModal from '@components/modal/Confirm.modal';
import { CheckCircleGreen, Delete, QuestionOrange } from '@icons/index';
import BarChartIcon from '@mui/icons-material/BarChart';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import InventoryIcon from '@mui/icons-material/Inventory';
import MapIcon from '@mui/icons-material/Map';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { IconButton, Stack, Typography } from '@mui/material';
import { auditsInformationService } from '@store/audits/audits-information/audit-information.service';
import { AuditInformation, AuditInformationStatusEnum } from '@store/audits/audits-information/audits-information.model';
import { AuditOperation } from '@store/audits/audits-operation/audits-operation.model';
import { auditsOperationService } from '@store/audits/audits-operation/audits-operation.service';
import { getTypeOf } from '@utils/getTypeOf';
import { ConfirmActionProps, OtherActionType } from '@utils/table/columnTypes/actions.columnType';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Observable, finalize } from 'rxjs';
import Contact from './contact/Contact';
import ContainerComponent from './container/Container.component';
import Data from './data/Data';
import Need from './need/Need';
import Observation from './observation/Observation';
import Site from './site/Site';

interface handleRenderProps {
  value: string;
  isExpended?: boolean;
  auditInformation?: AuditInformation;
  auditOperation?: AuditOperation;
  isAudit?: boolean;
}

const handleRender = (props: handleRenderProps) => {
  const { value, isExpended, auditInformation, auditOperation } = props;

  if (auditInformation) {
    switch (value) {
      case 'CONTACT':
        return <Contact auditInformation={auditInformation} />;
      case 'NEED':
        return <Need auditInformation={auditInformation} />;
      case 'DATA':
        return <Data auditInformation={auditInformation} />;
      case 'MAP':
        return <Site auditInformation={auditInformation} />;
      case 'OBSERVATION':
        return <Observation auditInformation={auditInformation} />;
    }
  } else {
    return (
      <ContainerComponent
        auditOperation={auditOperation!}
        isExpended={isExpended}
        oldValue={getTypeOf<AuditOperation>(auditOperation!.oldValue)}
      />
    );
  }
};

const getHeaderData = (type: string, auditOperation) => {
  switch (type) {
    case 'CONTACT':
      return { label: 'Contact site', color: '#61D2CE', icon: <PersonIcon sx={{ color: 'white', width: '18px' }} /> };
    case 'NEED':
      return {
        label: 'Besoins',
        color: '#F5BE55',
        icon: <VolunteerActivismIcon sx={{ color: 'white', width: '18px' }} />,
      };
    case 'DATA':
      return { label: 'Donnée', color: '#E55142', icon: <BarChartIcon sx={{ color: 'white', width: '18px' }} /> };
    case 'MAP':
      return { label: 'Plan du site', color: '#E44ABC', icon: <MapIcon sx={{ color: 'white', width: '18px' }} /> };
    case 'OBSERVATION':
      return { label: 'Observations', color: '#6FD969', icon: <SearchIcon sx={{ color: 'white', width: '18px' }} /> };
    case 'CONTAINER':
      return {
        label: auditOperation?.container?.name + ' - ' + auditOperation?.waste?.name,
        color: '#4F3FF5',
        icon: <InventoryIcon sx={{ color: 'white', width: '18px' }} />,
      };
  }
};

interface AuditsContentProps {
  auditInformation?: AuditInformation;
  auditOperation?: AuditOperation;
  isTemplate?: boolean;
  auditInformationType?: string;
  isDraggable?: boolean;
  isAudit?: boolean;
}

const AuditsContent = (props: AuditsContentProps) => {
  const { auditInformation, auditOperation, isTemplate = false, isDraggable } = props;
  const type = auditInformation ? auditInformation.type : 'CONTAINER';
  const [isExpanded] = useState(false);
  const headerData = getHeaderData(type, auditOperation);
  const { auditId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);

  const handleConfirmValidation =
    (type: OtherActionType, auditId: string, auditInformation?: AuditInformation, auditOperation?: AuditOperation) =>
      (handleStopLoading: () => void) => {
        let validationObservable: Observable<any> | undefined = undefined;
        switch (type) {
          case OtherActionType.DELETE:
            validationObservable = auditInformation?.id
              ? auditsInformationService.deleteAuditInformation(auditInformation.id, auditId)
              : auditsOperationService.deleteAuditOperation(auditOperation!.id, auditId);
            break;
        }
        validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
          next: () => {
            enqueueSnackbar(
              t(
                `audits.success.${auditOperation ? 'audit' : 'pre-audit'}.delete-${auditOperation ? 'container' : auditInformation?.type.toString().toLowerCase()
                }`
              ),
              { variant: 'success' }
            );
            setOpenConfirmAction(false);
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
      };

  const handleUpdate = (auditInformation?, auditOperation?) => {
    if (auditInformation) {
      navigate('add-data', {
        state: { auditInformation: auditInformation },
      });
    }
    if (auditOperation) {
      navigate('add-data', {
        state: { auditOperation: auditOperation },
      });
    }
  };

  return (
    <Stack
      direction="row"
      width="100%"
      px="15px"
      py="15px"
      justifyContent="space-between"
      borderRadius="4px"
      bgcolor="#F4F6F8"
    >
      <Stack justifyContent={'center'} alignItems="start" mr="3rem">
        <Stack direction="row" alignItems="center" spacing={1.3}>
          <Stack
            sx={{
              backgroundColor: headerData?.color,
              borderRadius: '3px',
              alignItems: 'center',
              padding: '4px',
              justifyContent: 'center',
              width: '25px',
              height: '25px',
            }}
          >
            {headerData?.icon}
          </Stack>
          <Typography fontSize="14px" fontWeight={700} color="#192133" noWrap>
            {headerData?.label}
          </Typography>
        </Stack>
      </Stack>
      <Stack width="100%" justifyContent="center">
        {auditInformation
          ? handleRender({ value: type, isExpended: isExpanded, auditInformation: auditInformation })
          : handleRender({ value: type, isExpended: isExpanded, auditOperation: auditOperation })}
      </Stack>
      {!isTemplate && (
        <Stack direction="row" justifyContent="end" alignItems={'center'} spacing={2} ml="2rem">
          {isDraggable ? (
            <>
              {auditInformation?.status &&
                (auditInformation?.status === AuditInformationStatusEnum.PENDING ? (
                  <QuestionOrange />
                ) : (
                  <CheckCircleGreen />
                ))}
              <IconButton sx={{ color: '#03053A' }} onClick={() => handleUpdate(auditInformation, auditOperation)}>
                <BorderColorIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  const handleDelete = () => {
                    setOpenConfirmAction({
                      type: OtherActionType.DELETE,
                      row: auditInformation,
                    });
                  };
                  handleDelete();
                }}
              >
                <Delete />
              </IconButton>
            </>
          ) : (
            <IconButton sx={{ color: '#03053A' }}>
              <DragHandleIcon />
            </IconButton>
          )}
          {/*{type === AuditInformationTypeEnum.OBSERVATION && (
            <IconButton onClick={() => setIsExpended(!isExpanded)}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}*/}
        </Stack>
      )}
      {!!openConfirmAction && (
        <ConfirmModal
          handleClose={() => setOpenConfirmAction(false)}
          description={t(
            `${OtherActionType.translationKey(openConfirmAction.type)}.${auditOperation ? 'audit' : 'pre-audit'}.${auditOperation
              ? auditOperation.type.toString().toLowerCase()
              : auditInformation?.type.toString().toLowerCase()
            }`
          )}
          handleValidate={handleConfirmValidation(openConfirmAction.type, auditId!, auditInformation, auditOperation)}
          title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
          validateButton={OtherActionType.label(openConfirmAction.type)}
        />
      )}
    </Stack>
  );
};

export default AuditsContent;
